<template>
  <div class="create-new-project">
    <div class="create-new-project-header">
      <span class="create-new-project-header-container">
        <div class="new-project-title d-inline-block">{{ $t('newProject.create.new.project') }}</div>
        <div class="cynergy-shield d-inline-block">
          <img src="../assets/logo-no-text.svg" />
        </div>
        <new-project-control-buttons
          :showCreateButton="currentStep === stepNames.length"
          :actionTitle="$t('newProject.create')"
          @onEscapeClicked="onEscapeClicked()"
          @onActionClicked="saveNewProject()"
        />
      </span>
    </div>
    <div class="content-area rectangle-area">
      <new-project-details
        v-if="currentStep === 1"
        :newProject="newProject"
        @setRequiredDataForStep="onDataChanged"
        :managers="managers"
      />
      <new-project-environment
        v-if="currentStep === 2"
        :newProject="newProject"
        @setRequiredDataForStep="onDataChanged"
      />
      <new-project-resources
        v-if="currentStep === 3"
        :newProject="newProject"
        @onChangeStep='onChangeStep'
        @setRequiredDataForStep="onDataChanged"
      />
      <new-project-bundles v-if="currentStep === 4" :newProject="newProject" @setRequiredDataForStep="onDataChanged" />
      <new-project-provider v-if="currentStep === 5" :newProject="newProject" @setRequiredDataForStep="onDataChanged" />
      <new-project-review v-if="currentStep === 6" :newProject="newProject" @setRequiredDataForStep="onDataChanged" />
      <new-project-scoping-started
        v-if="currentStep === 7"
      />
    </div>
    <new-project-stepper
      :currentStep="currentStep"
      :filledStepsCnt="filledStepsCnt"
      :visitedStepsCnt="visitedStepsCnt"
      :stepNames="stepNames"
      :allowNext="allowNext"
      @clicked="onChangeStep"
    />

    <yes-no-popup
      :visible-popup="showCloseNewProjectPopup"
      :title="'close.newProject.title'"
      :message="'close.newProject.message'"
      :yesCaption="'confirm.ok'"
      :noCaption="'confirm.cancel'"
      @clicked="onClickYesNoButton"
    />

    <message-popup
      :visible-popup="showErrorPopup"
      :title="'error.popup.title'"
      :message="'error.something.wrong.message'"
      :okCaption="$t('confirm.ok')"
      @clicked="onClickErrorMessageButton"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';

import store from '@/store';
import { views } from '@/router/constants';
import {
  emptyProject,
  initNewProjectNavigation,
  RESOURCES_TYPES,
  testBoxTypes,
  testModeTypes,
} from '@/constants/projects';
import NewProjectDetails from '@/components/newProject/NewProjectDetails.vue';
import NewProjectEnvironment from '@/components/newProject/NewProjectEnvironment.vue';
import NewProjectControlButtons from '@/components/newProject/NewProjectControlButtons.vue';
import NewProjectResources from '@/components/newProject/NewProjectResources.vue';
import NewProjectBundles from '@/components/newProject/NewProjectBundles.vue';
import NewProjectProvider from '@/components/newProject/NewProjectProvider.vue';
import NewProjectReview from '@/components/newProject/NewProjectReview.vue';
import NewProjectStepper from '@/components/newProject/NewProjectStepper.vue';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import MessagePopup from '@/components/popup/MessagePopup.vue';
import { validateIPAddress } from '@/utils/stringUtils';
import { ADD_PROJECT } from '@/store/project/actionTypes';
import { SET_NEW_PROJECT, SET_NEW_PROJECT_NAVIGATION } from '@/store/newProject/actionTypes';
import { LOAD_USERS, LOAD_WORKERS } from '@/store/user/actionTypes';
import NewProjectScopingStarted from '@/components/newProject/NewProjectScopingStarted.vue';

export default {
  name: 'NewProject',
  components: {
    NewProjectScopingStarted,
    NewProjectDetails,
    NewProjectEnvironment,
    NewProjectResources,
    NewProjectControlButtons,
    NewProjectBundles,
    NewProjectProvider,
    NewProjectReview,
    NewProjectStepper,
    YesNoPopup,
    MessagePopup,
  },
  data() {
    return {
      managers: [],
      showCloseNewProjectPopup: false,
      showErrorPopup: false,
      stepNames: [
        'newProject.project.details',
        'newProject.environment',
        'newProject.resources',
        'newProject.bundles',
        'newProject.date.service.provider',
        'newProject.review',
      ],
      allowNext: false,
    };
  },
  computed: {
    ...mapGetters(['users', 'profile', 'storedNewProject', 'storedNewProjectNavigation']),
    projectsRouteName() {
      return views.ACTIVITY_LOG;
    },
    newProject() {
      return this.storedNewProject;
    },
    currentStep() {
      return this.storedNewProjectNavigation.currentStep;
    },
    filledStepsCnt() {
      return this.storedNewProjectNavigation.filledStepsCnt;
    },
    visitedStepsCnt() {
      return this.storedNewProjectNavigation.visitedStepsCnt;
    },
  },
  async beforeMount() {
    await store.dispatch(LOAD_USERS);
    this.initManager();
    await this.initLoadWorkersIfNeed();
    this.allowNext = this.calculateStepsPermissions(Number(this.storedNewProjectNavigation.currentStep) + 1);
    this.prepareManagers();
  },
  methods: {
    initManager() {
      const storedManager = this.storedNewProject.projectDetailsData.manager;
      this.newProject.projectDetailsData.manager = storedManager || this.profile;
    },
    async initLoadWorkersIfNeed() {
      if (this.storedNewProject?.projectProviderData?.startDate) {
        const { startDate } = this.storedNewProject.projectProviderData;
        const { endDate } = this.storedNewProject.projectProviderData;
        await store.dispatch(LOAD_WORKERS, { startDate, endDate });
      }
    },
    async onChangeStep(value) {
      if (Number(value) === 7) {
        this.storedNewProjectNavigation.currentStep = Number(value);
        if (this.storedNewProjectNavigation.visitedStepsCnt < Number(value)) {
          this.storedNewProjectNavigation.visitedStepsCnt += 1;
        }
        this.allowNext = this.calculateStepsPermissions(3);
        await store.dispatch(SET_NEW_PROJECT_NAVIGATION, this.storedNewProjectNavigation);
      } else {
        const isPermitted = this.calculateStepsPermissions(Number(value));
        if (isPermitted) {
          if (this.storedNewProjectNavigation.visitedStepsCnt < Number(value)) {
            this.storedNewProjectNavigation.visitedStepsCnt += 1;
          }
          this.storedNewProjectNavigation.currentStep = Number(value);
          this.allowNext = this.calculateStepsPermissions(Number(value) + 1);
          await store.dispatch(SET_NEW_PROJECT_NAVIGATION, this.storedNewProjectNavigation);
        }
      }
    },
    async onDataChanged(stepNumber, needToResetNextSteps) {
      if (needToResetNextSteps) {
        this.resetNextSteps(stepNumber);
      }
      if (this.calculateStepsPermissions(Number(stepNumber) + 1)) {
        this.allowNext = true;
        this.refreshFilledStepsCntIfNeed(stepNumber);
      } else {
        this.allowNext = false;
      }
      await store.dispatch(SET_NEW_PROJECT, this.newProject);
    },
    calculateStepsPermissions(stepToGo) {
      if (!this.newProject?.projectDetailsData?.name?.trim().length) {
        return false;
      }
      switch (stepToGo) {
      case 1: {
        return true;
      }
      case 2: {
        const name = this.newProject.projectDetailsData.name?.trim();
        const testType = this.newProject.projectDetailsData.testType?.trim();
        const { manager } = this.newProject.projectDetailsData;
        const result = name?.length && testType?.length && manager;
        return Boolean(result);
      }
      case 3: {
        const data = this.newProject.projectEnvironmentData.configuration.data?.trim();
        const result = data?.length;
        return Boolean(result);
      }
      case 4: {
        const resourcesNotEmpty = this.newProject.projectResourcesData.resources?.length > 0;
        const resourceWithoutName = this.newProject.projectResourcesData.resources?.find(r => !r.value.trim().length);
        const allResourcesAutomaticallyScoped =
            this.newProject.projectEnvironmentData.mode !== testModeTypes.AUTOMATED ||
            this.newProject.projectResourcesData.resources?.every(res => !!res.automaticallyScoped);
        const result = resourcesNotEmpty && !resourceWithoutName && allResourcesAutomaticallyScoped;

        return Boolean(result);
      }
      case 5: {
        const data = this.newProject.projectBundlesData.data?.trim();
        const result = data?.length;
        return Boolean(result);
      }
      case 6: {
        const data = this.newProject.projectProviderData.serviceProviderId;
        const hours = this.newProject.projectProviderData.projectDurationHours;
        return Boolean(data?.length && hours);
      }
      case 7: {
        return true;
      }
      default:
        return false;
      }
    },
    refreshFilledStepsCntIfNeed(step) {
      if (this.storedNewProjectNavigation.filledStepsCnt < step) {
        this.storedNewProjectNavigation.filledStepsCnt += 1;
      }
    },
    saveNewProject() {
      const newProjectToCreate = this.preparedNewProjectToCreate();
      const options = { newProject: newProjectToCreate };
      store
        .dispatch(ADD_PROJECT, options)
        .then(async () => {
          await store.dispatch(SET_NEW_PROJECT, _.cloneDeep(emptyProject));
          await store.dispatch(SET_NEW_PROJECT_NAVIGATION, _.cloneDeep(initNewProjectNavigation));
          this.$router.push({ name: views.ACTIVITY_LOG });
        })
        .catch(() => {
          this.showErrorPopup = true;
        });
    },
    preparedNewProjectToCreate() {
      return {
        name: this.newProject.projectDetailsData.name,
        type: this.newProject.projectDetailsData.testType,
        comment: this.newProject.projectDetailsData.comment,
        managerId: this.newProject.projectDetailsData.manager.id,
        configurationType: this.newProject.projectEnvironmentData.configuration.type,
        testingType: this.newProject.projectEnvironmentData.configuration.data,
        resources: this.preparedResourcesToCreate(),
        bundle: this.newProject.projectBundlesData.data,
        startDate: this.newProject.projectProviderData.startDate,
        endDate: this.newProject.projectProviderData.endDate,
        workerId: this.newProject.projectProviderData.serviceProviderId,
        estimation: this.newProject.projectProviderData.projectDurationHours,
        budget: this.newProject.projectProviderData.projectTotalPrice,
      };
    },
    preparedResourcesToCreate() {
      return this.newProject.projectResourcesData.resources.map(res => ({
        name: res.value,
        type: validateIPAddress(res.value) ? RESOURCES_TYPES.IP : RESOURCES_TYPES.DOMAIN,
        credentials: this.processCredentials(res),
        features: this.processFeatures(res),
      }));
    },
    getNonEmptyCredentials(credentials) {
      return credentials.filter(cred => cred.username && cred.password);
    },
    processCredentials(resource) {
      if (this.newProject.projectEnvironmentData.configuration.data === testBoxTypes.BLACK_BOX) {
        return [];
      }

      return this.getNonEmptyCredentials(resource.credentials);
    },
    processFeatures(resource) {
      return resource.features.filter(feat => feat.selected).map(feat => ({ name: feat.value }));
    },
    onEscapeClicked() {
      this.showCloseNewProjectPopup = true;
    },
    async onClickYesNoButton(result) {
      this.showCloseNewProjectPopup = false;
      if (result) {
        await store.dispatch(SET_NEW_PROJECT, _.cloneDeep(emptyProject));
        await store.dispatch(SET_NEW_PROJECT_NAVIGATION, _.cloneDeep(initNewProjectNavigation));
        await this.$router.push({ name: views.ACTIVITY_LOG });
      }
    },
    onClickErrorMessageButton() {
      this.showErrorPopup = false;
    },
    resetNextSteps(stepNumber) {
      this.allowNext = false;
      this.storedNewProjectNavigation.visitedStepsCnt = stepNumber;
      this.storedNewProjectNavigation.filledStepsCnt = stepNumber - 1;
      this.processResetSteps(stepNumber);
    },
    processResetSteps(stepNumber) {
      const list = ['', 'projectEnvironmentData', 'projectBundlesData', 'projectProviderData'];
      // eslint-disable-next-line no-plusplus
      for (let i = stepNumber; i < list.length; i++) {
        this.newProject[list[i]] = { ...list[i], ..._.cloneDeep(emptyProject)[list[i]] };
      }
    },
    prepareManagers() {
      this.managers = this.users.map(user => ({ id: user.id, name: `${user.firstName} ${user.lastName}` }));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.create-new-project {
  height: 100%;
  padding-bottom: 1%;
  display: flex;
  background-color: var(--bg-secondary);
}

.create-new-project-header {
  position: fixed;
  width: 100%;
  top: 0;
  background-color: var(--bg-secondary);
  z-index: 200;

  .create-new-project-header-container {
    width: $create-new-project-rectangle-container-width;
    margin: auto;
    padding-top: rem(14);
    padding-bottom: rem(14);
    display: flex;
  }
}

.new-project-title {
  width: 33%;
  color: var(--text-primary);
  font-family: $font-family-bold;
  font-size: rem(22);
  font-weight: bold;
  line-height: rem(20);
  letter-spacing: 0;
  text-align: left;
  padding-top: rem(10);
  text-transform: capitalize;
}

.cynergy-shield {
  width: 33%;
  text-align: center;

  img {
    max-height: rem(44);
  }
}

.header-buttons {
  display: inline-block;
  width: 33%;

  .save-button {
    float: right;
    margin-top: rem(12);
    display: inline-block;
    cursor: pointer;
    height: rem(32);
    width: rem(66);
    border-radius: $radius-xs;
    background-color: var(--brand-primary);
    box-shadow: 0 rem(1) rem(1) 0 rgba(0, 0, 0, 0.14), 0 rem(2) rem(1) rem(-1) rgba(0, 0, 0, 0.12), 0 rem(1) rem(3) 0 rgba(0, 0, 0, 0.2);
    color: var(--text-on-brand);
    font-size: rem(12);
    font-weight: 500;
    letter-spacing: rem(1.07);
    line-height: rem(16);
    text-align: center;
    text-transform: uppercase;
    padding-top: rem(8);
  }

  .escape-link {
    float: right;
    cursor: pointer;
    margin-left: rem(20);
    text-align: right;
    font-size: rem(12);
    text-transform: uppercase;
    text-decoration: none;
    padding-top: rem(20);
    height: rem(16);
    color: var(--text-secondary);
    font-family: $font-family-medium;
    font-weight: 500;
    letter-spacing: rem(1.1);
  }
}

.rectangle-area {
  width: $create-new-project-rectangle-container-width;
  min-width: $create-new-project-rectangle-container-width;
  min-height: rem(716);
  border-radius: $radius-md;
  background-color: var(--bg-primary) !important;
  box-shadow: 0 0 rem(4) 0 rgba(14, 31, 51, 0.13);
  margin: auto;
  margin: rem(78) auto;
}
</style>
